import React, { useCallback, useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { DashboardHeader } from '../Dashboard/DashboardUi';
import { Modal } from '../ui/Modal';
import { Field, Select, Textarea } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { EtatEchec, Squelettes } from '../Organisation/EtatsPage';
import { useAuth } from '../../context/AuthContext';
import { ACTIONS, LIBELLES_ACTION, mesDroitsApi, messageErreur, type Action, type LigneDroits } from '../../lib/equipeApi';

/** Droits insuffisants (maquette L02-09) côté collaborateur : « Réservé au directeur » + « Demander l'accès ». */
const resume = (l: LigneDroits) => (ACTIONS.filter((a) => a !== 'sensibles' && l[a]).map((a) => LIBELLES_ACTION[a].toLowerCase()).join(', ') || 'aucun accès');

export const DemanderAccesPage: React.FC = () => {
    const { user } = useAuth();
    const [lignes, setLignes] = useState<LigneDroits[] | null>(null);
    const [directeur, setDirecteur] = useState('');
    const [erreur, setErreur] = useState<unknown>(null);
    const [cible, setCible] = useState<LigneDroits | null>(null);
    const [action, setAction] = useState<Action>('voir');
    const [texte, setTexte] = useState('');
    const [erreurModale, setErreurModale] = useState('');
    const [info, setInfo] = useState('');

    const charger = useCallback(async () => {
        setErreur(null);
        try { const r = await mesDroitsApi.droits(); setLignes(r.droits.lignes); setDirecteur(r.directeur); } catch (e) { setErreur(e); }
    }, []);
    useEffect(() => { void charger(); }, [charger]);

    const envoyer = async () => {
        if (!cible) return;
        try { await mesDroitsApi.demander({ module_name: cible.module_name, action, message: texte }); setCible(null); setTexte(''); setInfo(`Demande envoyée à la direction pour « ${cible.label} ».`); }
        catch (e) { setErreurModale(messageErreur(e)); }
    };

    return (
        <div className="space-y-4 max-w-[900px]">
            <DashboardHeader societe={user?.company_name || ''} titre="Demander un accès" sousTitre="Ce que vous pouvez faire, et ce que vous pouvez demander à la direction." />
            {info && <p role="status" className="text-[13px] font-medium text-state-ok">{info}</p>}
            {erreur ? <EtatEchec erreur={erreur} titre="Impossible d'afficher vos droits" onRetry={charger} /> : !lignes ? <Squelettes lignes={3} /> : (
                <>
                    <p className="flex items-center gap-2 text-[13px] text-content-muted"><Lock size={14} /> Réservé à la direction : {directeur ? `demandez-le à ${directeur}.` : 'envoyez une demande.'}</p>
                    <div className="bg-surface border border-border rounded-xl overflow-hidden">
                        {lignes.length === 0 && <p className="px-5 py-6 text-[13px] text-content-muted">Aucun module n'est activé pour votre établissement.</p>}
                        {lignes.map((l) => (
                            <div key={l.module_name} className="px-5 py-3 border-b border-border-soft last:border-b-0 flex flex-wrap items-center gap-3">
                                <div className="min-w-0 flex-1 basis-[220px]"><div className="text-[13px] font-semibold text-content">{l.label}</div><div className="text-[11.5px] text-content-muted">Vous pouvez : {resume(l)}</div></div>
                                <button type="button" className={pillSecondary(32)} onClick={() => { setCible(l); setAction(l.voir ? 'modifier' : 'voir'); setErreurModale(''); }}>Demander l'accès</button>
                            </div>
                        ))}
                    </div>
                </>
            )}
            <Modal open={!!cible} onClose={() => setCible(null)} size="md" title="Demander l'accès" subtitle={cible?.label}
                footer={<><button type="button" className={pillSecondary(36)} onClick={() => setCible(null)}>Annuler</button><button type="button" className={pillPrimary(36)} onClick={envoyer}>Envoyer la demande</button></>}>
                <div className="space-y-4">
                    <Field label="Ce que vous demandez">{(f) => <Select {...f} value={action} onChange={(e) => setAction(e.target.value as Action)}>{ACTIONS.filter((a) => a !== 'sensibles').map((a) => <option key={a} value={a}>{LIBELLES_ACTION[a]}</option>)}</Select>}</Field>
                    <Field label="Un mot pour la direction (facultatif)" error={erreurModale}>{(f) => <Textarea {...f} value={texte} onChange={(e) => setTexte(e.target.value)} maxLength={300} />}</Field>
                </div>
            </Modal>
        </div>
    );
};
